/** Formatting helpers (plan §3). */

/** The backend often serializes naive-UTC datetimes with no offset/Z (e.g.
 * "2026-08-10T23:06:42.108000") — `new Date()` on a string like that is
 * parsed as LOCAL time per the JS spec, not UTC, silently shifting every
 * date shown here by the browser's UTC offset (surfaced as archived-project
 * timestamps reading hours off). Every parse in this file routes through
 * here so a naive string is always treated as the UTC instant it actually
 * is. */
export function parseUtc(iso: string): Date {
  const hasOffset = /[zZ]$|[+-]\d{2}:\d{2}$/.test(iso)
  return new Date(hasOffset ? iso : `${iso}Z`)
}

/** Format a timestamp or ISO datetime string into a local time string (e.g. "10:23:47 AM").
 * Handles naive-UTC backend timestamps (e.g. "2026-08-10T23:06:42.108000") by parsing
 * them as UTC first, and gracefully handles already-formatted time strings. */
export function formatLocalTime(isoOrTime?: string): string {
  if (!isoOrTime || isoOrTime === 'Just now') return isoOrTime || ''

  // If it's an ISO datetime string (e.g. "2026-08-10T23:06:42" or "2026-08-10 23:06:42")
  if (/^\d{4}-\d{2}-\d{2}/.test(isoOrTime)) {
    const d = parseUtc(isoOrTime.replace(' ', 'T'))
    if (!isNaN(d.getTime())) {
      return d.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    }
  }

  // If it can be parsed as a full date (not just a time string like "10:23:47")
  if (isoOrTime.includes('-') || isoOrTime.includes('/')) {
    const d = new Date(isoOrTime)
    if (!isNaN(d.getTime())) {
      return d.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    }
  }

  return isoOrTime
}

/** Format a timestamp or ISO datetime string into local date and time string (e.g. "8/10/2026, 5:00:00 PM").
 * Handles naive-UTC backend timestamps (e.g. "2026-08-10T23:06:42.108000") by parsing
 * them as UTC first, and gracefully handles already-formatted datetime strings. */
export function formatLocalDateTime(isoOrTime?: string): string {
  if (!isoOrTime || isoOrTime === 'Just now') return isoOrTime || ''

  // If it's an ISO datetime string (e.g. "2026-08-10T23:06:42" or "2026-08-10 23:06:42")
  if (/^\d{4}-\d{2}-\d{2}/.test(isoOrTime)) {
    const d = parseUtc(isoOrTime.replace(' ', 'T'))
    if (!isNaN(d.getTime())) {
      return d.toLocaleString()
    }
  }

  // If it can be parsed as a full date (not just a time string like "10:23:47")
  if (isoOrTime.includes('-') || isoOrTime.includes('/')) {
    const d = new Date(isoOrTime)
    if (!isNaN(d.getTime())) {
      return d.toLocaleString()
    }
  }

  // If it's a time-only string (e.g. "10:23:47 AM" or "10:23:47"), prefix today's local date
  if (/^\d{1,2}:\d{2}(:\d{2})?(\s?[ap]m)?$/i.test(isoOrTime.trim())) {
    return `${new Date().toLocaleDateString()}, ${isoOrTime.trim()}`
  }

  return isoOrTime
}

/** List-row date (concept .cr-date): "Jul 14", or "Oct 8, 2025" once the
 * year differs. */
export function shortDate(iso: string, nowMs = Date.now()): string {
  const then = parseUtc(iso)
  const sameYear = then.getFullYear() === new Date(nowMs).getFullYear()
  // en-US pinned: the concept shows month-first ("Jul 14", "Oct 8, 2025").
  return then.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  })
}

/** Palette row date (reference command menu, 2026-07-28): "4 hours ago" /
 * "7 days ago" within a week, then shortDate ("Jul 20" / "Oct 8, 2025"). */
export function agoDate(iso: string, nowMs = Date.now()): string {
  const mins = Math.max(
    1,
    Math.round((nowMs - parseUtc(iso).getTime()) / 60_000),
  )
  if (mins < 60) return mins === 1 ? '1 minute ago' : `${mins} minutes ago`
  const hours = Math.round(mins / 60)
  if (hours < 24) return hours === 1 ? '1 hour ago' : `${hours} hours ago`
  const days = Math.round(hours / 24)
  if (days <= 7) return days === 1 ? '1 day ago' : `${days} days ago`
  return shortDate(iso, nowMs)
}

/** Full calendar date: "July 30, 2026" (en-US pinned like the others). */
export function longDate(iso: string): string {
  return parseUtc(iso).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

/** File size for chips/list rows: "512 B", "33 KB", "2.2 MB". */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** Elapsed time for the agent-activity header — concept "Worked for 1m 12s"
 * (line 1189), sub-minute as "21.4s" and sub-second as "412ms". */
export function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`
  const seconds = ms / 1000
  if (seconds < 60) return `${seconds.toFixed(1)}s`
  const mins = Math.floor(seconds / 60)
  return `${mins}m ${Math.round(seconds - mins * 60)}s`
}

/** Compact relative timestamp for sidebar/list rows: "2h", "3d", "Jul 12". */
export function relativeTime(iso: string, nowMs = Date.now()): string {
  const then = parseUtc(iso).getTime()
  const mins = Math.max(0, Math.round((nowMs - then) / 60_000))
  if (mins < 1) return 'now'
  if (mins < 60) return `${mins}m`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.round(hours / 24)
  if (days < 7) return `${days}d`
  return parseUtc(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  })
}

/** Backend entity refs arrive as markdown links with custom schemes
 * (`[T51BE00595](lift:T51BE00595)`). Display the label only so Streamdown
 * does not render `T51BE00595 [blocked]`. http(s)/mailto links are kept. */
export function unwrapEntityMarkdownLinks(text: string): string {
  return text.replace(
    /\[([^\]]+)\]\(\s*(?!https?:|mailto:)[a-zA-Z][\w+.-]*:[^)]*\)/gi,
    '$1',
  )
}

export function markdownToPlainText(text?: string | null): string {
  return unwrapEntityMarkdownLinks(text || '')
    .replace(/```[^\n]*\n([\s\S]*?)```/g, '$1')
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')
    .replace(/^\s{0,3}>\s?/gm, '')
    .replace(/(\*\*\*|___|\*\*|__)(.+?)\1/gs, '$2')
    .replace(/(?<![*\w])\*(?=\S)([\s\S]+?)(?<=\S)\*(?![*\w])/g, '$1')
    .replace(/(?<![_\w])_(?=\S)([\s\S]+?)(?<=\S)_(?![_\w])/g, '$1')
    .replace(/~~(.+?)~~/gs, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/[ \t]+$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/** Undo extra JSON-style escaping when the API stores `\u2014` / `\"` as
 * literal characters instead of real punctuation (common on ask_user fields). */
export function unescapeDisplayText(text: string): string {
  if (!text.includes('\\')) return text
  return text
    .replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) =>
      String.fromCharCode(parseInt(hex, 16)),
    )
    .replace(/\\(["'\\/])/g, '$1')
    .replace(/\\n/g, '\n')
    .replace(/\\r/g, '\r')
    .replace(/\\t/g, '\t')
}

/** Compare chat bubbles and turn.question after entity-link unwrap. */
export function normalizeQuestionKey(text?: string | null): string {
  return unwrapEntityMarkdownLinks(text || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
}

export function questionsMatch(a?: string | null, b?: string | null): boolean {
  if (!a || !b) return false
  return normalizeQuestionKey(a) === normalizeQuestionKey(b)
}

const SUMMARY_PLACEHOLDER = 'Analyzing network telemetry and stream content…'

const SUMMARY_UNAVAILABLE = new Set([
  '',
  'Analyzing network telemetry…',
  SUMMARY_PLACEHOLDER,
  'Analysis summary.',
])

/** True when chat / TurnCard should paint the SUMMARY card (user 2026-09-02). */
export function isSummaryAvailable(text?: string): boolean {
  return !SUMMARY_UNAVAILABLE.has((text || '').trim())
}

const H2_HEADING = /(?:^|\n)##(?!#)\s+\S/
const UNDERSTANDING_OPEN = /<understanding[\s>/]/i

/** kind=text / type=text with no `<understanding>` and no `##` heading. */
export function isUnheadedProse(text?: string | null): boolean {
  const t = (text || '').trim()
  if (!isSummaryAvailable(t)) return false
  if (UNDERSTANDING_OPEN.test(t)) return false
  if (H2_HEADING.test(t)) return false
  return true
}

/** Chat-list body for stream + history. Unheaded kind=text is the summary. */
export function assistantChatListBody(opts: {
  summary?: string
  summaryTitle?: string
  answerText?: string
  explanation?: string
}): string {
  const summary = (stripAbortNotice(opts.summary) || opts.summary || '').trim()
  const answer = (opts.answerText || '').trim()
  const unheaded =
    !opts.summaryTitle && !opts.explanation && isUnheadedProse(answer)
  if (unheaded) return answer
  if (summary) return summary
  const fromAnswer = cleanSummaryText(answer)
  return isSummaryAvailable(fromAnswer) ? fromAnswer : summary
}

/** Chat-list bubble: prefer live analysis while streaming; on pause, lift
 * summary from analysis when the bubble still holds a placeholder. */
export function resolveAssistantMessageText(opts: {
  bubbleText?: string
  analysis?: {
    summary?: string
    summaryTitle?: string
    answerText?: string
    explanation?: string
  }
  isStreaming?: boolean
  hasTextDelta?: boolean
}): string {
  const bubble = opts.bubbleText || ''
  const fromAnalysis = opts.analysis ? assistantChatListBody(opts.analysis) : ''
  const analysisBody = stripAbortNotice(fromAnalysis)
  const bubbleBody = stripAbortNotice(bubble)

  if (
    opts.isStreaming &&
    (opts.hasTextDelta || isSummaryAvailable(analysisBody))
  ) {
    if (isSummaryAvailable(analysisBody)) return fromAnalysis
    if (isSummaryAvailable(bubbleBody)) return bubble
    return fromAnalysis || bubble
  }

  if (opts.analysis && isSummaryAvailable(analysisBody)) {
    if (isAbortedStreamText(bubble) && !isAbortedStreamText(fromAnalysis)) {
      if (isSummaryAvailable(bubbleBody)) return bubble
      const notice = abortNoticeText(bubble)
      return notice ? `${fromAnalysis}\n\n*${notice}*` : fromAnalysis
    }
    return isAbortedStreamText(bubble) ? bubble : fromAnalysis
  }

  return bubble
}

/** Strip understanding tags and non-summary sections so chat-list and
 * TurnCard summaries share the same cleaned markdown body. */
export function cleanSummaryText(raw?: string, isStreamData?: boolean): string {
  const base = raw || (isStreamData ? SUMMARY_PLACEHOLDER : '')
  if (!base) return SUMMARY_PLACEHOLDER

  let clean = base
    // Keep in sync with stripUnderstandingTags (chatParser): stop at
    // `\n\n## Summary` when `</understanding>` is missing.
    .replace(
      /<understanding>[\s\S]*?(?:<\/understanding>|(?=\r?\n\r?\n## Summary)|$)/gi,
      '',
    )
    .trim()

  clean = clean
    .replace(
      /(?:^|\n)(?:#+\s*|\*\*\s*)?(?:Analysis|Key Insights|Findings)(?::|\*\*|\b)?\s*[\s\S]*?(?=\n#+|\n\*\*|\n(?:Recommendation|Recommended Play|Recommended Plays|Next Steps|Action Items)(?::|\*\*|\b)|$)/gi,
      '',
    )
    .trim()

  clean = clean
    .replace(
      /(?:^|\n)(?:#+\s*|\*\*\s*)?(?:Recommendation|Recommended Play|Recommended Plays|Next Steps|Action Items)(?::|\*\*|\b)?\s*[\s\S]*?(?=\n#+|\n\*\*|$)/gi,
      '',
    )
    .trim()

  const match = clean.match(
    /(?:^|\n)(?:#+\s*|\*\*\s*)?(?:Executive\s*)?(?:Summary|Overview)(?::|\*\*|\b)?\s*([\s\S]*)/i,
  )
  if (match && match[1] && match[1].trim()) {
    clean = match[1].trim()
  }
  const withoutAbort = stripAbortNotice(clean)
  if (withoutAbort) return withoutAbort
  return clean || SUMMARY_PLACEHOLDER
}

/** Drop stop/interrupt footers so a retried answer is not stuck on
 * “Agent's response was interrupted.” */
export function stripAbortNotice(text?: string): string {
  return (text || '')
    .replace(
      /\n*[_*]{0,2}Agent'?s response was interrupted\.?[_*]{0,2}\s*/gi,
      '',
    )
    .replace(
      /\n*[_*]{0,2}Response generation was (?:stopped|aborted|cancelled|canceled|interrupted)\.?[_*]{0,2}\s*/gi,
      '',
    )
    .replace(/\n*[_*]{0,2}Stream aborted by the user\.?[_*]{0,2}\s*/gi, '')
    .trim()
}

/** Drop leading 1. / - markers when the whole body is a list so Streamdown
 * renders paragraphs (same gutters as ANALYSIS). User 2026-09-02: numbers
 * on Recommendations can be shown or ignored — ignored here. */
export function flattenListMarkers(content: string): string {
  const text = (content || '').trim()
  if (!text || !/^(?:\d+\.|[-*+])\s+/.test(text)) return text
  return text
    .split(/(?:^|\n)(?:\d+\.|[-*+])\s+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .join('\n\n')
}

/** Drop a leading heading that repeats the section title so the box
 * does not start with heading margin (RECOMMENDATIONS looked gappier).
 * Recommendation bodies that are only a numbered/bulleted list lose the
 * markers so the box matches ANALYSIS. */
export function cleanSectionContent(title: string, content: string): string {
  let text = (content || '').replace(/^\uFEFF/, '').trim()
  if (!text || !title) return text
  const label = title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  text = text.replace(
    new RegExp(
      `^(?:#{1,6}\\s*)?(?:\\*\\*)?${label}(?:\\*\\*)?\\s*:?\\s*(?:\\n+|$)`,
      'i',
    ),
    '',
  )
  text = text.trim()
  if (/recommend|next step|action item/i.test(title)) {
    text = flattenListMarkers(text)
  }
  return text
}

/** Live stream stop marker emitted when the user aborts mid-turn. */
export const AGENT_INTERRUPTED_STREAM_TEXT =
  "_Agent's response was interrupted._"

export function hasAgentInterruptedStreamText(
  ...texts: (string | null | undefined)[]
): boolean {
  return texts.some((t) => t?.includes(AGENT_INTERRUPTED_STREAM_TEXT))
}

/** Restart in TurnCard — live aborted session or persisted API interrupt. */
export function shouldShowTurnRestart(opts: {
  turn: {
    id?: string
    question?: string
    chatId?: string
    summary?: string
    answerText?: string
    responseId?: string
    wasStreamInterrupted?: boolean
    isLoading?: boolean
  }
  activeStreamSession?: {
    isAborted?: boolean
    assistantMsgId?: string
    activeAnalysis?: { id?: string } | null
    query?: string
    chatId?: string | null
  } | null
  /** GET /conversation/{id}/stream_status — cancelled allows Retry. */
  streamStatus?: string | null
}): boolean {
  if (opts.turn.isLoading) return false

  if (
    opts.activeStreamSession?.isAborted &&
    sessionTargetsTurn(opts.activeStreamSession, opts.turn)
  ) {
    return true
  }

  return Boolean(opts.turn.wasStreamInterrupted)
}

/** True only for an aborted/stopped stream notice — not ordinary
 * analysis prose that happens to contain “failed” or “stopped”. */
export function isAbortedStreamText(text?: string): boolean {
  const raw = text || ''
  if (!raw) return false
  const lower = raw.toLowerCase()
  return (
    lower.includes('response generation was stopped') ||
    lower.includes('stream aborted') ||
    lower.includes('stream was aborted') ||
    /[_*]{0,2}agent'?s response was interrupted\.?[_*]{0,2}/i.test(raw) ||
    /(?:^|\n)\s*\*?(?:response generation was (?:stopped|aborted|cancelled|canceled|interrupted))\*?/i.test(
      raw,
    )
  )
}

const ABORT_NOTICE_RE =
  /[_*]{0,2}(?:Agent'?s response was interrupted|Response generation was (?:stopped|aborted|cancelled|canceled|interrupted)|Stream (?:was )?aborted(?: by the user)?)\.?[_*]{0,2}/i

export function abortNoticeText(
  ...texts: (string | null | undefined)[]
): string {
  for (const text of texts) {
    const match = (text || '').match(ABORT_NOTICE_RE)
    if (match) {
      const notice = match[0].replace(/[_*]/g, '').trim()
      return notice.endsWith('.') ? notice : `${notice}.`
    }
  }
  return ''
}

/** Abort / interrupt banner — live stream only; API/history text is ignored. */
export function shouldShowAbortNotice(opts: {
  isLoading?: boolean
  /** Live SSE turn or a parked aborted session — not persisted API reload. */
  isStreamContext?: boolean
  texts?: (string | null | undefined)[]
}): boolean {
  if (!opts.isStreamContext) return false
  if (opts.isLoading) return false
  const sources = (opts.texts || []).filter((t): t is string =>
    Boolean(t && t.trim()),
  )
  if (sources.length === 0) return false
  if (!abortNoticeText(...sources)) return false
  const body = stripAbortNotice(sources.join('\n\n'))
  return !isSummaryAvailable(body)
}

/** Retry / Paused apply to this turn only — not every card that shares
 * the parked session’s chatId (user 2026-09-02). */
export function sessionTargetsTurn(
  session:
    | {
        query?: string
        chatId?: string | null
        assistantMsgId?: string
        activeAnalysis?: { id?: string } | null
      }
    | null
    | undefined,
  turn: { id?: string; question?: string; chatId?: string },
): boolean {
  if (!session) return false
  if (session.activeAnalysis?.id && session.activeAnalysis.id === turn.id) {
    return true
  }
  if (session.assistantMsgId) {
    if (
      turn.id === session.assistantMsgId ||
      turn.id === `analysis-${session.assistantMsgId}`
    ) {
      return true
    }
  }
  if (
    session.query &&
    turn.question &&
    session.query.trim().toLowerCase() === turn.question.trim().toLowerCase()
  ) {
    if (!session.chatId || !turn.chatId || session.chatId === turn.chatId) {
      return true
    }
  }
  return false
}

/** Retry Response only on the aborted assistant — not completed
 * siblings, and not a history chat while another stream is parked. */
export function shouldShowRetryResponse(opts: {
  sender: string
  text?: string
  messageId: string
  readOnly?: boolean
  isStreaming?: boolean
  isAborted?: boolean
  abortedAssistantId?: string
  abortedChatId?: string | null
  currentChatId?: string | null
}): boolean {
  if (opts.readOnly || opts.isStreaming || opts.sender !== 'assistant') {
    return false
  }

  const body = stripAbortNotice(opts.text)
  if (isSummaryAvailable(body) && !isAbortedStreamText(opts.text)) {
    return false
  }

  const sessionApplies =
    !opts.abortedChatId ||
    !opts.currentChatId ||
    opts.abortedChatId === opts.currentChatId
  const targetId = sessionApplies ? opts.abortedAssistantId : undefined

  if (targetId && opts.messageId === targetId && opts.isAborted) {
    return true
  }
  if (isAbortedStreamText(opts.text)) {
    if (targetId && opts.messageId !== targetId) return false
    return true
  }
  return false
}

/** Consecutive assistants after a user — regenerate/retry siblings. */
export function followingAssistantIndexes<T extends { sender: string }>(
  messages: T[],
  userIndex: number,
): number[] {
  const idxs: number[] = []
  for (let i = userIndex + 1; i < messages.length; i++) {
    if (messages[i].sender !== 'assistant') break
    idxs.push(i)
  }
  return idxs
}

/** Latest completed sibling; otherwise the last assistant in the run. */
export function preferredAssistantIndex<
  T extends { sender: string; text: string },
>(messages: T[], userIndex: number): number | null {
  const idxs = followingAssistantIndexes(messages, userIndex)
  if (idxs.length === 0) return null
  const completed = [...idxs]
    .reverse()
    .find((i) => !isAbortedStreamText(messages[i].text))
  return completed ?? idxs[idxs.length - 1]
}

function owningQuestion<T extends { sender: string; text: string }>(
  messages: T[],
  assistantIndex: number,
): string {
  for (let i = assistantIndex - 1; i >= 0; i--) {
    if (messages[i].sender === 'user') return messages[i].text
  }
  return ''
}

export function hasLaterCompletedAssistant<
  T extends { sender: string; text: string },
>(messages: T[], fromIndex: number, question?: string): boolean {
  for (let i = fromIndex + 1; i < messages.length; i++) {
    if (
      messages[i].sender !== 'assistant' ||
      isAbortedStreamText(messages[i].text)
    ) {
      continue
    }
    if (question === undefined) return true
    if (questionsMatch(question, owningQuestion(messages, i))) return true
  }
  return false
}

/** Pairing for history/refresh: drop an interrupt notice once the SAME
 * question has a real answer later (ask_user follow-up or retry-as-new-user).
 * A question that was abandoned rather than retried keeps its notice, so the
 * turn can still offer Retry. */
export function preferredVisibleAssistantIndex<
  T extends { sender: string; text: string },
>(messages: T[], userIndex: number): number | null {
  const idx = preferredAssistantIndex(messages, userIndex)
  if (idx === null) return null
  if (
    isAbortedStreamText(messages[idx].text) &&
    hasLaterCompletedAssistant(messages, idx, messages[userIndex]?.text ?? '')
  ) {
    return null
  }
  return idx
}
